import type { NextPage, GetStaticPropsContext } from 'next'
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Image,
  Link,
  Text,
  VStack,
  useStyleConfig,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { PromotionSection } from '@/components/PromotionSection'
import { CompassMiningIcon } from '@/components/icons/CompassMiningIcon'
import { GearIcon } from '@/components/icons/GearIcon'
import { StacksIcon } from '@/components/icons/StacksIcon'
import { CandleSticksIcon } from '@/components/icons/CandleSticksIcon'
import { GogglesIcon } from '@/components/icons/GogglesIcon'
import { CheckIcon } from '@/components/icons/CheckIcon'
import { BriefCaseIcon } from '@/components/icons/BriefCaseIcon'
import { RobotIcon } from '@/components/icons/RobotIcon'
import { CommunicationChannelIcon } from '@/components/icons/CommunicationChannelIcon'
import { SUPPORT_FACILITY_APPLICATION_URL } from '@/constants'
import { useTranslations } from 'next-intl'
import SeoHead from '@/components/seo-head'

const FacilityApplicationPage: NextPage = () => {
  const styles = useStyleConfig('Box', { variant: 'white' })
  const t = useTranslations()

  return (
    <>
      <SeoHead
        title={t('facility-application.seo.title')}
        breadcrumbTitle={t('facility-application.seo.breadcrumb')}
        description={t('facility-application.seo.description')}
      />
      <Box __css={styles} overflow="hidden">
        <Hero />
        <WhatWhyCompass />
        <HostingSteps />
        <GotYouCovered />
        <Box
          mt={{ base: '60px', md: '80px', lg: '117px' }}
          height={{ base: '296px', md: '420px', xl: '550px' }}
          bgImage="/img/host-banner-middle@2x.jpeg"
          backgroundSize="cover"
          backgroundRepeat="no-repeat"
          backgroundPosition="bottom"
        />
        <HostingPlacements />
        <FAQs />
        <Promotion />
        <JoinCompass />
      </Box>
    </>
  )
}

const Hero = () => {
  const t = useTranslations()
  return (
    <Box
      height={{ md: '535px', lg: '580px' }}
      bgImage={{ base: 'none', md: '/img/host-banner-top@2x.jpeg' }}
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundPosition="bottom"
    >
      <Center width="100%" height="100%">
        <ContainerBase p={{ base: '32px 16px', lg: '0 33px' }}>
          <Flex justifyContent={{ base: 'center', lg: 'flex-end' }}>
            <Box
              bg="white"
              borderRadius="8px"
              textAlign={{ base: 'center', lg: 'left' }}
              boxShadow={{
                base: 'none',
                md: '0px 8px 16px -6px rgba(24, 39, 75, 0.08), 0px 20px 50px rgba(0, 0, 0, 0.1)',
              }}
              padding={{ base: '36px 12px', md: '36px 48px', lg: '36px' }}
              width={{ base: '', md: '500px', lg: '476px' }}
            >
              <Text
                as="h1"
                color="black"
                fontWeight="700"
                fontSize={{ base: '32px', md: '48px' }}
                lineHeight={{ base: '39px', md: '60px' }}
              >
                {t('facility-application.hero.block1')}
              </Text>
              <Text
                color="black"
                fontWeight="500"
                fontSize={{ base: '14px', md: '18px' }}
                lineHeight={{ base: '20px', md: '22px' }}
                mt={{ base: '18px', md: '18px' }}
              >
                {t.rich('facility-application.hero.block2', {
                  br: () => <br className="show-mobile" />,
                })}
              </Text>
              <NextLink href={SUPPORT_FACILITY_APPLICATION_URL} passHref>
                <Link isExternal _hover={{ textDecor: 'none' }}>
                  <Button
                    variant="brand"
                    width="100%"
                    mt={{ base: '36px', md: '52px' }}
                  >
                    {t('facility-application.get-started')}
                  </Button>
                </Link>
              </NextLink>
            </Box>
          </Flex>
        </ContainerBase>
      </Center>
      <Box
        height="200px"
        display={{ base: 'block', md: 'none' }}
        bgImage="/img/host-banner-top@2x.jpeg"
        backgroundSize="cover"
        backgroundRepeat="no-repeat"
        backgroundPosition="bottom"
      />
    </Box>
  )
}

const WhatWhyCompass = () => {
  const t = useTranslations()
  return (
    <ContainerBase
      mt={{ base: '52px', md: '86px', lg: '96px' }}
      padding={{ md: '0 134px', lg: 0 }}
    >
      <PromotionSection
        gap={{ base: '32px', md: '60px', lg: '200px' }}
        contents={[
          {
            media: <CompassMiningIcon boxSize={{ base: '32px', md: '44px' }} />,
            title: t('facility-application.what-is-compass.title'),
            description: t('facility-application.what-is-compass.description'),
          },
          {
            media: (
              <GearIcon
                color="brand.600"
                boxSize={{ base: '32px', md: '44px' }}
              />
            ),
            title: t('facility-application.why-host-with-compass.title'),
            description: t(
              'facility-application.why-host-with-compass.description'
            ),
          },
        ]}
      />
    </ContainerBase>
  )
}

const HostingSteps = () => {
  const t = useTranslations()
  return (
    <ContainerBase mt={{ base: '80px', md: '80px', lg: '116px' }}>
      <Text
        textAlign={{ base: 'left', md: 'center' }}
        fontWeight="700"
        fontSize={{ base: '32px', md: '48px' }}
        lineHeight={{ base: '39px', md: '60px' }}
      >
        {t('facility-application.hosting-steps.title')}
      </Text>
      <Flex
        flexDirection={{ base: 'column', lg: 'row' }}
        gap={{ base: '32px', md: '60px', xl: '115px' }}
        mt={{ base: '32px', md: '60px', lg: '80px' }}
        alignItems="center"
      >
        <Image
          boxSize={{ base: '296px', md: '400px', lg: '450px', xl: '500px' }}
          src="/img/hosting-iphone.png"
        />
        <Box padding={{ md: '0 100px', lg: 0 }}>
          <PromotionSection
            orientation="column"
            gap={{ base: '32px', md: '42px', lg: '52px' }}
            itemProps={{
              flexDirection: 'row',
              gap: { base: '32px', md: '22px', lg: '32px' },
            }}
            contents={[
              {
                media: (
                  <StacksIcon
                    color="brand.600"
                    boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                  />
                ),
                title: t('facility-application.hosting-steps.step1.title'),
                description: t(
                  'facility-application.hosting-steps.step1.description'
                ),
              },
              {
                media: (
                  <CandleSticksIcon
                    color="brand.600"
                    boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                  />
                ),
                title: t('facility-application.hosting-steps.step2.title'),
                description: t(
                  'facility-application.hosting-steps.step2.description'
                ),
              },
              {
                media: (
                  <GogglesIcon
                    color="brand.600"
                    boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                  />
                ),
                title: t('facility-application.hosting-steps.step3.title'),
                description: t(
                  'facility-application.hosting-steps.step3.description'
                ),
              },
            ]}
          />
        </Box>
      </Flex>
    </ContainerBase>
  )
}

const GotYouCovered = () => {
  const t = useTranslations()
  return (
    <ContainerBase
      mt={{ base: '80px', md: '80px', lg: '116px' }}
      padding={{ md: '0 110px', lg: 0 }}
    >
      <Divider
        width={{ base: '70px', md: '100px' }}
        height="2px"
        margin="0 auto"
        backgroundColor="#000"
        borderBottomColor="#000"
      />
      <Text
        textAlign={{ base: 'left', md: 'center' }}
        fontWeight="700"
        fontSize={{ base: '32px', md: '48px' }}
        lineHeight={{ base: '39px', md: '60px' }}
        mt="32px"
      >
        {t('facility-application.got-you-covered.title')}
      </Text>
      <Flex
        mt={{ base: '32px', md: '60px' }}
        gap={{ base: '32px', md: '52px', xl: '130px' }}
        flexDirection={{ base: 'column', lg: 'row' }}
      >
        <Text
          fontSize={{ base: '14px', md: '16px' }}
          lineHeight={{ base: '20px', md: '23px' }}
          maxW={{ base: '100%', md: '500px' }}
        >
          {t.rich('facility-application.got-you-covered.content', {
            br: () => <br />,
          })}
        </Text>
        <VStack
          alignItems="flex-start"
          spacing={{ base: '22px', md: '20px', lg: '23px' }}
        >
          {[1, 2, 3, 4].map((item) => {
            return (
              <HStack key={`check-${item}`} alignItems="center" spacing="16px">
                <CheckIcon color="brand.600" width="24px" height="24px" />
                <Text
                  fontWeight="600"
                  fontSize={{ base: '16px', md: '16px' }}
                  lineHeight={{ base: '23px', md: '23px' }}
                >
                  {t(
                    `facility-application.got-you-covered.checklist.item${item}`
                  )}
                </Text>
              </HStack>
            )
          })}
        </VStack>
      </Flex>
    </ContainerBase>
  )
}

const HostingPlacements = () => {
  const t = useTranslations()
  return (
    <ContainerBase mt={{ md: '80px', lg: '116px' }}>
      <Text
        textAlign={{ base: 'left', md: 'center' }}
        fontWeight="700"
        fontSize={{ base: '32px', md: '48px' }}
        lineHeight={{ base: '39px', md: '50px', lg: '60px' }}
        mt={{ base: '60px', md: '22px', lg: '32px' }}
      >
        {t('facility-application.hosting-placements.title')}
      </Text>
      <Box padding={{ md: '0 100px', lg: 0 }}>
        <PromotionSection
          mt={{ base: '52px', md: '65px', lg: '60px' }}
          gap={{ base: '32px', md: '40px', lg: '80px' }}
          contents={[
            {
              media: (
                <BriefCaseIcon
                  color="brand.600"
                  boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                />
              ),
              title: t(
                'facility-application.hosting-placements.section1.title'
              ),
              description: t(
                'facility-application.hosting-placements.section1.description'
              ),
            },
            {
              media: (
                <RobotIcon
                  color="brand.600"
                  boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                />
              ),
              title: t(
                'facility-application.hosting-placements.section2.title'
              ),
              description: t(
                'facility-application.hosting-placements.section2.description'
              ),
            },
            {
              media: (
                <CommunicationChannelIcon
                  color="brand.600"
                  boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                />
              ),
              title: t(
                'facility-application.hosting-placements.section3.title'
              ),
              description: t(
                'facility-application.hosting-placements.section3.description'
              ),
            },
          ]}
        />
      </Box>
    </ContainerBase>
  )
}

const FAQs = () => {
  const t = useTranslations()
  return (
    <ContainerBase mt={{ base: '60px', md: '80px', lg: '106px' }}>
      <Divider />
      <Text
        textAlign={{ base: 'left', md: 'center' }}
        fontWeight="700"
        fontSize={{ base: '32px', md: '48px' }}
        lineHeight={{ base: '39px', md: '60px' }}
        mt={{ base: '60px', md: '80px', lg: '106px' }}
      >
        {t('facility-application.faqs.title')}
      </Text>
      <Flex
        width="100%"
        justifyContent="space-between"
        mt={{ base: '52px', md: '60px', lg: '80px' }}
        flexWrap="wrap"
        flexDirection={{ base: 'column', lg: 'row' }}
        padding={{ md: '0 100px', lg: 0 }}
      >
        {[1, 2, 3].map((item) => {
          return (
            <Box
              key={`faq-${item}`}
              maxWidth={{ base: '100%', lg: '400px', xl: '500px' }}
              mb={{ base: '32px', md: '52px' }}
            >
              <Text
                fontWeight="600"
                fontSize={{ base: '20px', md: '16px', lg: '20px' }}
                lineHeight={{ base: '24px', md: '20px', lg: '24px' }}
              >
                {t(`facility-application.faqs.q${item}.title`)}
              </Text>
              <Text
                mt={{ base: '16px', md: '14px', lg: '16px' }}
                fontSize={{ base: '16px', md: '14px', lg: '16px' }}
                lineHeight={{ base: '20px', md: '20px', lg: '23px' }}
              >
                {t(`facility-application.faqs.q${item}.answer`)}
              </Text>
            </Box>
          )
        })}
      </Flex>
    </ContainerBase>
  )
}

const Promotion = () => {
  const t = useTranslations()

  const sections = [
    {
      key: 'procurement-services',
      image: '/img/host-hardware.jpeg',
      link: '/procurement-service',
    },
    {
      key: 'become-a-vendor',
      image: '/img/host-facilities.jpeg',
      link: SUPPORT_FACILITY_APPLICATION_URL,
      isExternal: true,
    },
    {
      key: 'verify-your-facility',
      image: '/img/host-facility.jpeg',
      link: SUPPORT_FACILITY_APPLICATION_URL,
      isExternal: true,
    },
  ]
  const contents = sections.map((item) => {
    return {
      media: (
        <Box
          width={{ base: '100%', md: '320px', lg: '100%' }}
          height={{ md: '100%', lg: '140px', xl: '160px' }}
        >
          <Image
            width="100%"
            height="100%"
            objectFit="cover"
            borderRadius="8px"
            src={item.image}
          />
        </Box>
      ),
      title: (
        <NextLink href={item.link} passHref>
          <Link isExternal={item.isExternal} variant="black">
            <Text as="span">{t(`facility-application.${item.key}.title`)}</Text>
          </Link>
        </NextLink>
      ),
      description: (
        <Text width={{ md: '100%', lg: '280px', xl: '320px' }}>
          {t.rich(`facility-application.${item.key}.description`, {
            br: () => <br />,
            link: (children) => {
              return (
                <NextLink href={item.link} passHref>
                  <Link isExternal={item.isExternal}>
                    <Text as="span">{children}</Text>
                  </Link>
                </NextLink>
              )
            },
          })}
        </Text>
      ),
    }
  })
  return (
    <ContainerBase mt={{ base: '80px', md: '86px', lg: '64px' }}>
      <PromotionSection
        contents={contents}
        gap={{ base: '52px', md: '40px', xl: '80px' }}
        itemProps={{
          flexDirection: { base: 'column', md: 'row', lg: 'column' },
          alignItems: 'center',
        }}
      />
    </ContainerBase>
  )
}

const JoinCompass = () => {
  const styles = useStyleConfig('Box', { variant: 'gray' })
  const t = useTranslations()
  return (
    <Box
      height={{ base: '580px', md: 'unset' }}
      mt={{ base: '80px', md: '86px', lg: '116px' }}
      __css={styles}
    >
      <Box
        width="100%"
        position="relative"
        height={{ base: '100%', md: '405px' }}
        pt={{ base: '60px', md: 'unset' }}
      >
        <ContainerBase
          height="100%"
          position="relative"
          zIndex="10"
          display="flex"
          flexDirection="column"
          justifyContent={{ base: 'flex-start', md: 'center' }}
        >
          <Text
            fontWeight="700"
            fontSize={{ base: '32px', md: '28px', xl: '38px' }}
            lineHeight={{ base: '39px', md: '36px', xl: '46px' }}
          >
            {t('facility-application.ready-to-join-compass.title')}
          </Text>
          <Text
            fontWeight="600"
            fontSize={{ base: '16px', md: '16px', xl: '20px' }}
            lineHeight={{ base: '20px', md: '20px', xl: '28px' }}
            mt={{ base: '18px', md: '14px', xl: '18px' }}
            zIndex="10000"
          >
            {t('facility-application.ready-to-join-compass.description')}
          </Text>
          <NextLink href={SUPPORT_FACILITY_APPLICATION_URL} passHref>
            <Link isExternal _hover={{ textDecor: 'none' }}>
              <Button
                variant="brand"
                mt={{ base: '32px', md: '42px', xl: '52px' }}
                width={{ base: '100%', md: '210px' }}
              >
                {t('facility-application.get-started')}
              </Button>
            </Link>
          </NextLink>
        </ContainerBase>
        <Box
          position="absolute"
          right={{ base: '0', md: '-20%', lg: '0' }}
          bottom="0"
          zIndex="5"
        >
          <Image
            src="/img/planet.png"
            css={{
              '@media(max-width: 500px)': {
                transform: 'scale(1.5)',
              },
            }}
          />
          <Box
            position="absolute"
            top="19%"
            right="41%"
            css={{
              '@media(max-width: 500px)': {
                right: '30%',
              },
            }}
            bgImage="/img/planet-mark.svg"
            backgroundRepeat="no-repeat"
            backgroundSize="contain"
            padding="6px"
            height="64px"
          >
            <HStack>
              <Image
                src="/img/facility-planet-rockdale.jpeg"
                height="30px"
                width="40px"
              />
              <Text color="gray.350" fontSize="10px">
                Rockdale, United States
              </Text>
            </HStack>
          </Box>
          <Box
            position="absolute"
            right="20%"
            bottom="23%"
            css={{
              '@media(max-width: 500px)': {
                right: '5%',
                bottom: '5%',
              },
            }}
            bgImage="/img/planet-mark.svg"
            backgroundRepeat="no-repeat"
            backgroundSize="contain"
            padding="6px"
            height="64px"
          >
            <HStack>
              <Image
                src="/img/facility-planet-newyork.jpeg"
                height="30px"
                width="40px"
              />
              <Text color="gray.350" fontSize="10px">
                New York, United States
              </Text>
            </HStack>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
    },
  }
}

export default FacilityApplicationPage
